import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { useEstadoCartera } from '@/dominio/cuentas-por-cobrar/useCuentasPorCobrar'
import { EncabezadoPantalla } from './-pantalla'
import type { IdSeccionCartera, IrACartera } from './-cuentas-por-cobrar/comun'
import { ResumenCartera } from './-cuentas-por-cobrar/Resumen'
import { ClientesCartera } from './-cuentas-por-cobrar/Clientes'
import { RegistrarCobro } from './-cuentas-por-cobrar/Cobrar'
import { RecibosCartera } from './-cuentas-por-cobrar/Recibos'
import { AnticiposCartera } from './-cuentas-por-cobrar/Anticipos'
import { RecurrentesCartera } from './-cuentas-por-cobrar/Recurrentes'
import { RecordatoriosCartera } from './-cuentas-por-cobrar/Recordatorios'
import { ConfiguracionCartera } from './-cuentas-por-cobrar/Configuracion'

/**
 * Cuentas por cobrar (`docs/adr/0027-cuentas-por-cobrar-y-recurrentes.md`): lo
 * vendido a crédito, quién lo debe, los recibos con que se cobra, los anticipos,
 * los recordatorios y las facturas que se emiten solas.
 *
 * Es del plan base: no hace falta la contabilidad. Una pantalla con secciones,
 * como la contabilidad y la nómina; la sección, el cliente abierto, el recibo y
 * la recurrente van en la URL para poder pasar el enlace a una ficha concreta.
 */

const SECCIONES: readonly { id: IdSeccionCartera; etiqueta: string }[] = [
  { id: 'resumen', etiqueta: 'Resumen' },
  { id: 'clientes', etiqueta: 'Clientes' },
  { id: 'cobrar', etiqueta: 'Registrar cobro' },
  { id: 'recibos', etiqueta: 'Recibos' },
  { id: 'anticipos', etiqueta: 'Anticipos' },
  { id: 'recurrentes', etiqueta: 'Recurrentes' },
  { id: 'recordatorios', etiqueta: 'Recordatorios' },
  { id: 'configuracion', etiqueta: 'Configuración' },
]

function esSeccion(valor: unknown): valor is IdSeccionCartera {
  return SECCIONES.some((s) => s.id === valor)
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function uuid(valor: unknown): string | undefined {
  return typeof valor === 'string' && UUID.test(valor) ? valor : undefined
}

interface BusquedaCartera {
  seccion?: IdSeccionCartera
  cliente?: string
  recibo?: string
  recurrente?: string
}

export const Route = createFileRoute('/cuentas-por-cobrar')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaCartera => ({
    seccion: esSeccion(busqueda.seccion) && busqueda.seccion !== 'resumen' ? busqueda.seccion : undefined,
    cliente: uuid(busqueda.cliente),
    recibo: uuid(busqueda.recibo),
    recurrente: uuid(busqueda.recurrente),
  }),
  component: PantallaCuentasPorCobrar,
})

function PantallaCuentasPorCobrar() {
  const estado = useEstadoCartera()
  const navegar = useNavigate({ from: Route.fullPath })
  const busqueda = Route.useSearch()
  const seccion = busqueda.seccion ?? 'resumen'

  const irA: IrACartera = (s, abrir) =>
    void navegar({
      search: { seccion: s === 'resumen' ? undefined : s, cliente: abrir?.cliente, recibo: abrir?.recibo, recurrente: abrir?.recurrente },
      replace: true,
    })

  if (estado.isLoading) {
    return <LineaEsqueleto ancho="w-1/2" />
  }

  const e = estado.data

  if (!e?.puedeVer) {
    return (
      <div className="flex max-w-[900px] flex-col gap-4">
        <EncabezadoPantalla titulo="Cuentas por cobrar" />
        <EstadoVacio
          variante="primera-vez"
          mensaje="Su rol no ve la cartera. La ven el administrador, quien factura y el contador; pídale acceso al administrador del contribuyente."
        />
      </div>
    )
  }

  // Lo que el rol no puede hacer no se enseña; el servidor lo vuelve a negar en cada ruta.
  const visibles = SECCIONES.filter((s) => (s.id !== 'cobrar' || e.puedeCobrar) && (s.id !== 'configuracion' || e.puedeConfigurar))

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Cuentas por cobrar"
        descripcion="Lo vendido a crédito entra solo al emitirse. Aquí se cobra con recibo, se aplican anticipos y notas de crédito, y se le recuerda al cliente lo que vence."
      />

      <nav aria-label="Secciones de cuentas por cobrar" className="overflow-x-auto border-b border-gris-300">
        <ul className="flex gap-1">
          {visibles.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                aria-current={seccion === s.id ? 'page' : undefined}
                onClick={() => irA(s.id)}
                className={cx(
                  'inline-flex h-[var(--alto-control)] items-center whitespace-nowrap border-b-2 px-3 text-cuerpo',
                  seccion === s.id ? 'border-marca font-medium text-marca' : 'border-transparent text-gris-600 hover:text-gris-900',
                )}
              >
                {s.etiqueta}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {seccion === 'resumen' ? <ResumenCartera estado={e} irA={irA} /> : null}
      {seccion === 'clientes' ? <ClientesCartera estado={e} clienteAbierto={busqueda.cliente ?? null} irA={irA} /> : null}
      {seccion === 'cobrar' && e.puedeCobrar ? <RegistrarCobro clienteInicial={busqueda.cliente ?? null} irA={irA} /> : null}
      {seccion === 'recibos' ? <RecibosCartera estado={e} reciboAbierto={busqueda.recibo ?? null} irA={irA} /> : null}
      {seccion === 'anticipos' ? <AnticiposCartera estado={e} irA={irA} /> : null}
      {seccion === 'recurrentes' ? <RecurrentesCartera estado={e} abierta={busqueda.recurrente ?? null} irA={irA} /> : null}
      {seccion === 'recordatorios' ? <RecordatoriosCartera estado={e} irA={irA} /> : null}
      {seccion === 'configuracion' && e.puedeConfigurar ? <ConfiguracionCartera /> : null}
    </div>
  )
}
